import {isValidDate} from '@/components/commons/input-date/utils'
import {ButtonLink} from '@/components/ui/button-link/ButtonLink'
import {FormCardSection} from '@/components/ui/form-atoms/FormAtoms'
import {GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {Text} from '@/components/ui/text/Text'
import {formatLocaleDate} from '@/dayjs'
import {useGetLastAccess} from '@/features/accesses/queries/useGetLastAccess'
import {CyclePeriodicityHypothesis} from '@/features/medical-reports/types'
import {periodicityHypothesisMapToLabel} from '@/features/medical-reports/utils'
import {useGetPatientLastInfo} from '@/features/patients/services/useGetPatientLastInfo'
import {useGetPlannedAccessesDetails} from '@/features/planned-accesses/queries/useGetPlannedAccessesDetails'
import {projectSectorToLabel, projectStatusToLabel} from '@/features/projects/utils'
import {
    TreatmentRequestScore,
    TreatmentRequestSmall,
    TreatmentRequestTreatmentValidationSchema
} from '@/features/treatment-requests/types'
import {Trans, useTranslation} from '@/translations/i18n'
import {useEffect, useMemo} from 'react'
import {useFormContext} from 'react-hook-form'
import {remapDiseaseHierarchy} from '../../utils'
import {Tooltip} from '@/components/ui/tooltip/Tooltip'
import {InfoCircleIcon} from '@/components/ui/icon/Icon'
import {useTheme} from 'styled-components'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'

export const TreatmentRequestProjectResumptionCard = ({
    patientId,
    type
}: {
    patientId: string
    type: Exclude<TreatmentRequestSmall['type'], 'new_takeover'>
}) => {
    const {t} = useTranslation()
    const {setError, setValue, clearErrors} = useFormContext<TreatmentRequestTreatmentValidationSchema>()
    const {palette} = useTheme()

    const {
        data: lastInfo,
        isLoading: isLoadingLastInfo,
        isError,
        error
    } = useGetPatientLastInfo({
        patientId: patientId,
        type
    })

    const {data: plannedAccesses, isLoading: isLoadingPlannedAccesses} = useGetPlannedAccessesDetails({
        urlParams: {
            projectId: lastInfo?.project?.id ?? ''
        },
        options: {
            enabled: !!lastInfo?.project?.id,
            staleTime: 0,
            gcTime: 0
        }
    })

    const {data: lastAccess, isLoading: isLoadingLastAccess} = useGetLastAccess({
        params: {
            projectId: lastInfo?.project?.id ?? '',
            groupName: 'treatments'
        },
        options: {
            enabled: !!lastInfo?.project?.id
        }
    })

    const accessesRefToPlanned = useMemo(() => {
        return plannedAccesses?.treatments.find(treatment => {
            if (!lastAccess?.accessType) return false
            return treatment.accessType == lastAccess.accessType.accessType
        })
    }, [lastAccess, plannedAccesses])

    const isLoading = useMemo(
        () => isLoadingPlannedAccesses || isLoadingLastInfo || isLoadingLastAccess,
        [isLoadingPlannedAccesses, isLoadingLastInfo, isLoadingLastAccess]
    )

    const reopeningNextCycle = useMemo(
        () => lastInfo?.cyclicalReportClosingInfo.find(report => report.name == 'reopeningNextCycle'),
        [lastInfo]
    )

    const cyclePeriodicityHypothesis = useMemo(
        () => lastInfo?.cyclicalReportClosingInfo.find(report => report.name == 'cyclePeriodicityHypothesis'),
        [lastInfo]
    )

    useEffect(() => {
        if (isError) {
            if (error?.response?.data.code == 'project-not-found') {
                setError('type', {message: t('treatment_requests:type_error'), type: 'custom'})
            }
        } else {
            clearErrors('type')
        }
    }, [isError, error?.code])

    useEffect(() => {
        if (lastInfo?.project?.id) {
            if (lastInfo.project.status == 'closed') {
                setValue('project', {id: lastInfo.project.id}, {shouldDirty: true})
            } else {
                setError('type', {
                    message: t('treatment_requests:existing_project_error', {
                        status: t(projectStatusToLabel[lastInfo.project.status]).toLowerCase()
                    }),
                    type: 'custom'
                })
            }
        } else {
            setValue('project', null, {shouldDirty: true})
        }
    }, [lastInfo?.project?.id, lastInfo?.project?.status])

    useEffect(() => {
        if (lastInfo?.treatmentRequest && lastInfo.project?.status === 'closed') {
            const mappedDisease = remapDiseaseHierarchy(lastInfo.treatmentRequest.disease).reverse()
            const scores = lastInfo.treatmentRequest.scores.filter(
                (score: TreatmentRequestScore) => score.score != null
            )
            setValue('sector', lastInfo.treatmentRequest.sector, {shouldDirty: true})
            setValue('authorization', lastInfo.treatmentRequest.authorization, {shouldDirty: true})
            setValue('diseases', mappedDisease, {shouldDirty: true})
            setValue('scores', scores, {shouldDirty: true})
        }
    }, [lastInfo?.treatmentRequest, lastInfo?.project?.status])

    if (isError || (!isLoading && lastInfo?.project?.status !== 'closed')) return null

    return (
        <FormCardSection columns={3} gap={2} padding={3} fullWidth>
            {isLoading ? (
                Array.from({length: 3}).map((_, index) => (
                    <GridItem key={index} direction="column" gap={1.5}>
                        <Skeleton height={20} width={100} />
                        <Skeleton height={20} width={'100%'} />
                    </GridItem>
                ))
            ) : (
                <>
                    <GridItem direction="column" gap={1.5}>
                        <Text palette="neutral" shade="500" size="textSm">
                            {t('treatment_requests:last_treatment_date')}
                        </Text>
                        <Text weight="500" palette="neutral" shade="950" size="textSm">
                            {lastAccess?.date ? formatLocaleDate(lastAccess.date, 'DD/MM/YYYY') : '-'}
                        </Text>
                    </GridItem>
                    <GridItem direction="column" gap={1.5}>
                        <Text palette="neutral" shade="500" size="textSm">
                            {t('treatment_requests:last_intervention_modality')}
                        </Text>
                        <Text weight="500" palette="neutral" shade="950" size="textSm">
                            {lastInfo?.project && lastInfo?.project.sector != 'mixed'
                                ? t(projectSectorToLabel[lastInfo.project.sector])
                                : accessesRefToPlanned?.sector
                                  ? t(projectSectorToLabel[accessesRefToPlanned.sector])
                                  : '-'}
                        </Text>
                    </GridItem>
                    <GridItem direction="column" gap={1.5}>
                        <Text palette="neutral" shade="500" size="textSm">
                            {accessesRefToPlanned?.frequency == 'weekly'
                                ? t('accesses:weekly_frequency')
                                : t('accesses:monthly_frequency')}
                        </Text>
                        <Text weight="500" palette="neutral" shade="950" size="textSm">
                            {accessesRefToPlanned?.numberOfAccesses
                                ? accessesRefToPlanned.numberOfAccesses.toString()
                                : '-'}
                        </Text>
                    </GridItem>
                    {lastInfo?.project?.type == 'cyclic' && (
                        <>
                            <GridItem direction="column" gap={1.5}>
                                <Text palette="neutral" shade="500" size="textSm">
                                    {t('medical_records:reopen_next_cycle')}
                                </Text>
                                <Text weight="500" palette="neutral" shade="950" size="textSm">
                                    {reopeningNextCycle?.value
                                        ? isValidDate(reopeningNextCycle.value, 'YYYY-MM-DD')
                                            ? formatLocaleDate(reopeningNextCycle.value, 'DD/MM/YYYY')
                                            : t('errors:invalid_date')
                                        : '-'}
                                </Text>
                            </GridItem>
                            <GridItem direction="column" gap={1.5}>
                                <Text palette="neutral" shade="500" size="textSm">
                                    {t('projects:cycle_count')}
                                </Text>
                                <Text weight="500" palette="neutral" shade="950" size="textSm">
                                    {cyclePeriodicityHypothesis?.value
                                        ? t(
                                              periodicityHypothesisMapToLabel[
                                                  cyclePeriodicityHypothesis.value as CyclePeriodicityHypothesis
                                              ]
                                          )
                                        : '-'}
                                </Text>
                            </GridItem>
                            <GridItem direction="column" gap={1.5}>
                                <Text palette="neutral" shade="500" size="textSm">
                                    {t('treatment_requests:last_project')}
                                </Text>
                                <Text
                                    style={{display: 'inline-flex', gap: 4}}
                                    weight="500"
                                    palette="neutral"
                                    shade="950"
                                    size="textSm"
                                >
                                    <Trans
                                        i18nKey="treatment_requests:pri_n_expiration_date_link"
                                        components={{
                                            a: (
                                                <ButtonLink
                                                    shape="text"
                                                    variant="tertiary"
                                                    to={`/projects/${lastInfo?.project.id}`}
                                                    target="_blank"
                                                />
                                            )
                                        }}
                                        values={{
                                            code: lastInfo?.project.code.toString(),
                                            date: formatLocaleDate(lastInfo.project.startDate, 'DD/MM/YYYY')
                                        }}
                                    />
                                </Text>
                            </GridItem>
                            <GridItem direction="column" gap={1.5}>
                                <Text palette="neutral" shade="500" size="textSm">
                                    {t('treatment_requests:project_referring_doctor')}
                                </Text>
                                <Text weight="500" palette="neutral" shade="950" size="textSm">
                                    {lastInfo.treatmentRequest?.referringDoctor?.fullName}
                                </Text>
                            </GridItem>
                            <GridItem direction="column" gap={1.5}>
                                <Text palette="neutral" shade="500" size="textSm">
                                    {t('commons:therapist', {count: 2})}
                                </Text>
                                {lastInfo.treatmentRequest?.therapists &&
                                lastInfo.treatmentRequest?.therapists?.length > 0 ? (
                                    <Flexbox gap={1} align="center">
                                        <Text weight="500" palette="neutral" shade="950" size="textSm">
                                            {lastInfo.treatmentRequest?.therapists?.length == 1
                                                ? lastInfo.treatmentRequest?.therapists[0]?.fullName
                                                : t('treatment_requests:therapists_and_others', {
                                                      therapist: lastInfo.treatmentRequest?.therapists[0].fullName,
                                                      othersCount: lastInfo.treatmentRequest?.therapists?.length - 1
                                                  })}
                                        </Text>
                                        {lastInfo.treatmentRequest?.therapists?.length > 1 && (
                                            <Tooltip
                                                variant="light"
                                                content={
                                                    <ul>
                                                        {lastInfo.treatmentRequest?.therapists?.map(therapist => (
                                                            <li key={therapist.id}>{therapist.fullName}</li>
                                                        ))}
                                                    </ul>
                                                }
                                            >
                                                <InfoCircleIcon color={palette.neutral[500]} size={14} />
                                            </Tooltip>
                                        )}
                                    </Flexbox>
                                ) : (
                                    <Text weight="500" palette="neutral" shade="950" size="textSm">
                                        -
                                    </Text>
                                )}
                            </GridItem>
                        </>
                    )}
                </>
            )}
        </FormCardSection>
    )
}
